// Device-proof America/Vancouver time helpers.
// WHY: Intl / dayjs-tz / MUI `timezone` all resolve the zone via the DEVICE's
// ICU tz database, and that database differs from device to device. These
// helpers compute the Vancouver offset from a fixed rule instead, and format
// with pure UTC arithmetic - output is identical on every device.
// RULE: British Columbia is on permanent daylight time, UTC-7, from
// 2026-03-08T10:00:00Z (the last spring-forward) onward. Clocks did not move
// on 2026-11-01 and never will again under the current law (BC Gov release
// 2026AG0013-000209, IANA tzdata 2026b).
// HISTORICAL: before that instant the old US/Canada rule applies, kept only so
// past instants still render correctly: PDT (UTC-7) from the 2nd Sunday of
// March at 2:00 AM local (10:00 UTC) to the 1st Sunday of November at 2:00 AM
// local (09:00 UTC), PST (UTC-8) otherwise.
// Devices with current tzdata agree with this rule; devices with stale tzdata
// are the ones that drift.
// If the law ever changes again, update THIS ONE FUNCTION:
// pacificOffsetMinutes (and keep djr-be's helper identical).
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const MIN = 60 * 1000;

const nthSundayUtcMs = (year, monthIdx, nth, hourUtc) => {
  const first = new Date(Date.UTC(year, monthIdx, 1));
  const firstSundayDate = 1 + ((7 - first.getUTCDay()) % 7);
  return Date.UTC(year, monthIdx, firstSundayDate + (nth - 1) * 7, hourUtc);
};

// First instant of BC permanent daylight time (UTC-7): 2026-03-08T10:00:00Z.
export const PERMANENT_DST_FROM_MS = Date.UTC(2026, 2, 8, 10);

// Offset in minutes to ADD to UTC to get Vancouver wall time. -420 (UTC-7)
// for every instant from PERMANENT_DST_FROM_MS on; for earlier instants the
// historical US/Canada rule (-480 PST / -420 PDT).
export const pacificOffsetMinutes = (utcMs) => {
  if (utcMs >= PERMANENT_DST_FROM_MS) return -420;
  const y = new Date(utcMs).getUTCFullYear();
  const dstStart = nthSundayUtcMs(y, 2, 2, 10); // 2nd Sun Mar, 10:00 UTC
  const dstEnd = nthSundayUtcMs(y, 10, 1, 9); // 1st Sun Nov, 09:00 UTC
  return utcMs >= dstStart && utcMs < dstEnd ? -420 : -480;
};

// A dayjs (UTC-mode) object whose wall-clock fields equal Pacific local time.
// USE ONLY for formatting / wall-clock comparisons against other pacificWall()
// values - its .valueOf() is shifted, NOT the real instant.
export const pacificWall = (isoOrDateOrMs) => {
  // Guard against the classic misuse: passing a formatted wall-clock STRING
  // (e.g. "Saturday, November 28, 2026 4:00 PM") instead of a zone-explicit ISO
  // instant. A non-ISO string is parsed in the DEVICE's zone below and shifts by
  // the device offset - the exact bug that made PDFs render 1h early on mobile.
  if (
    process.env.NODE_ENV !== "production" &&
    typeof isoOrDateOrMs === "string" &&
    !/^\d{4}-\d{2}-\d{2}/.test(isoOrDateOrMs)
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[pacificTime] non-ISO string passed to pacificWall/formatPacific: ` +
        `"${isoOrDateOrMs}". This parses in the device's local zone and will ` +
        `drift by the device offset. Pass a raw UTC ISO instant (e.g. ` +
        `event.startDateTime), not a formatted date string.`
    );
  }
  const ms = dayjs(isoOrDateOrMs).valueOf();
  return dayjs.utc(ms + pacificOffsetMinutes(ms) * MIN);
};

export const formatPacific = (isoOrDateOrMs, fmt) =>
  pacificWall(isoOrDateOrMs).format(fmt);

export const pacificNowWall = () => pacificWall(Date.now());

// Convert Pacific wall-clock -> real UTC Date. `wall` = plain dayjs whose
// fields ARE the Pacific wall time (zone-agnostic). Mirrors djr-be's
// pacificWallToUtc: resolve offset against wall-treated-as-UTC (close enough
// for correct DST lookup).
export const pacificWallToUtcDate = (wall) => {
  const refUtcMs = Date.UTC(
    wall.year(),
    wall.month(),
    wall.date(),
    wall.hour(),
    wall.minute(),
    0,
    0
  );
  return new Date(refUtcMs - pacificOffsetMinutes(refUtcMs) * MIN);
};

export const pacificWallToUtcIso = (wall) =>
  pacificWallToUtcDate(wall).toISOString();

// ---------------------------------------------------------------------------
// Date-only values ("YYYY-MM-DD")
//
// Per spec, `new Date("2026-08-29")` parses as UTC MIDNIGHT, so anything west
// of Greenwich renders it a day early. Lead-log `eventDate` / `nextActionDate`
// are date-only Pacific CALENDAR DAYS, not instants - they must never make a
// round trip through a zone. These helpers keep them as calendar days.
// ---------------------------------------------------------------------------

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

// Returns a UTC-mode dayjs whose wall-clock fields are the Pacific calendar
// day (date-only in) or the Pacific wall time (instant in) - the same contract
// as pacificWall(). null when the value is empty or unparseable.
export const parseDateOnlyOrInstant = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string" && DATE_ONLY_RE.test(value.trim())) {
    // No zone conversion at all: the fields ARE the calendar day.
    const d = dayjs.utc(`${value.trim()}T00:00:00Z`);
    return d.isValid() ? d : null;
  }
  const wall = pacificWall(value);
  return wall.isValid() ? wall : null;
};

// Display formatter. A date-only value in gives that same calendar day out, on
// every device. null when there is nothing to render - callers pick their own
// placeholder ("-" in the lead log, "" in the reviews grid).
export const formatDateOnlyOrInstant = (value, fmt = "MMM D, YYYY") => {
  const d = parseDateOnlyOrInstant(value);
  return d ? d.format(fmt) : null;
};

// True when a "due" value falls before TODAY in Pacific. Compares CALENDAR
// DAYS, never raw timestamps - comparing the parsed instant to Date.now() is
// what made a lead due today turn red at 5:00 PM the day before.
export const isPastPacificDay = (dateOnly) => {
  const d = parseDateOnlyOrInstant(dateOnly);
  if (!d) return false;
  return d.format("YYYY-MM-DD") < pacificNowWall().format("YYYY-MM-DD");
};

// Sort key for a mixed date-only / instant column. Day-granular and stable,
// so ordering is identical on every device.
export const dateSortValue = (value) =>
  parseDateOnlyOrInstant(value)?.valueOf() ?? null;
